import { RenderSimplePerson } from "@/blocks/persons-and-users/User"
import {
  InfiniteScrollingTable,
  SortingStateAndUpdater,
  TheasoftColumnDef,
} from "@/routes/-components/custom-tanstack-table"
import { ContractTypeTreeSelect } from "@components/tree/contract-type-tree-select"
import { ContractApprovalStatusMultiSelect } from "@components/multi-select/ContractApprovalStatusMultiSelect"
import { DepartmentOrWorkareaTreeSelect } from "@components/department-workarea-tree-select"
import { Page } from "@components/Page"
import { InfinitePersonMultiSelect } from "@components/SelectFromQuery/InfinitePersonMultiSelect"
import { createFileRoute } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { ContractDto, getPaginatedContracts } from "@/orval/gen"
import { getFormattedDate } from "@/utils/get-formatted-date"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { ProductionMultiSelect } from "@components/multi-select/ProductionMultiSelect"
import { DateRangePickerInputWithSeasonPresets } from "@components/date-picker/DateRangePickerInputWithSeasonPresets"
import { DatesRangeValue } from "@mantine/dates"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { SortingState } from "@tanstack/react-table"
import { getDateOnly } from "@components/date-only"

export interface Search {
  reminderDetailOpened?: boolean
  reminderId?: number
  startDate?: string | null
  endDate?: string | null
  departmentTreeNodes?: string[]
  contractTypeTreeNodes?: string[]
  contractStatusIds?: number[]
  personIds?: number[]
  productionIds?: number[]
  sorting?: SortingState
}

export const Route = createFileRoute("/app/contracts/contracts/")({
  component: ContractTablePage,
  validateSearch: (search: Search) =>
    ({
      ...search,
      departmentTreeNodes: search.departmentTreeNodes?.length
        ? search.departmentTreeNodes
        : undefined,
      contractTypeTreeNodes: search.contractTypeTreeNodes?.length
        ? search.contractTypeTreeNodes
        : undefined,
      contractStatusIds: search.contractStatusIds?.length
        ? search.contractStatusIds
        : undefined,
      personIds: search.personIds?.length ? search.personIds : undefined,
      productionIds: search.productionIds?.length
        ? search.productionIds
        : undefined,
      sorting: search.sorting ?? [{ id: "startDate", desc: false }],
      startDate:
        typeof search.startDate === "undefined"
          ? (getDateOnly() as string)
          : search.startDate,
      endDate:
        typeof search.endDate === "undefined"
          ? (getDateOnly() as string)
          : search.endDate,
      reminderDetailOpened: search.reminderDetailOpened,
      reminderId: search.reminderId,
    }) as Search,
})

export function ContractTablePage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const updateSearch = (input: Partial<Search>) => {
    navigate({
      search: {
        ...search,
        ...input,
      },
    })
  }
  const dateRange: DatesRangeValue<string> = React.useMemo(
    () => [search.startDate ?? null, search.endDate ?? null],
    [search.startDate, search.endDate]
  )
  const { t } = useTranslation("app_server")

  const columns = React.useMemo<TheasoftColumnDef<ContractDto>[]>(
    () => [
      {
        accessorKey: "contractId",
        header: "ID",
      },
      {
        accessorKey: "personId",
        header: t("Person"),
        cell: ({ row }) => {
          const { personId } = row.original
          return <RenderSimplePerson suspense={false} personId={personId} />
        },
      },
      {
        accessorKey: "departmentName",
        header: t("Department"),
      },
      {
        accessorKey: "startDate",
        header: t("Start"),
        cell: ({ row }) => getFormattedDate(row.original.startDate),
      },
      {
        accessorKey: "endDate",
        header: t("End"),
        cell: ({ row }) => getFormattedDate(row.original.endDate),
      },
      {
        accessorKey: "approvalStatus",
        header: t("Status"),
        cell: ({ row }) => row.original.approvalStatus?.name,
      },
      {
        accessorKey: "contractTypeName",
        header: t("ContractType"),
      },
      {
        accessorKey: "productionRoles",
        header: `${t("Production")}/${t("Role")}`,
        cell: ({ row }) => row.original.productionRoles.join(", "),
        enableSorting: false,
      },
    ],
    [t]
  )
  const [filterVisible, setFilterVisible] = useUserLocalStorage({
    key: "contracts-contracts-filter-visible",
    defaultValue: false,
  })
  const xs = useBreakpoints().xs
  const filterProps = {
    w: xs ? "100%" : 300,
    clearable: true,
    searchable: true,
  }
  const queryOptions = useResponsiveFilterQueryOptions(filterVisible)

  const queryResult = useDefaultInfiniteQuery(
    getPaginatedContracts,
    (pageRequest) => ({
      ...pageRequest,
      ...search,
    }),
    queryOptions
  )
  const sortingState: SortingStateAndUpdater = [
    search.sorting ?? [],
    (updater) =>
      typeof updater === "function"
        ? updateSearch({ sorting: updater(search.sorting ?? []) })
        : updateSearch({ sorting: updater }),
  ]
  return (
    <Page
      buttonGroupProps={{
        children: (
          <>
            <ResponsiveFilterGroup.Button
              visible={filterVisible}
              onChange={() => setFilterVisible(!filterVisible)}
              filters={{
                people: search.personIds,
                departments: search.departmentTreeNodes,
                approvalStatuses: search.contractStatusIds,
                contractTypes: search.contractTypeTreeNodes,
                dateRange: search.startDate || search.endDate,
                productions: search.productionIds,
              }}
            ></ResponsiveFilterGroup.Button>
          </>
        ),
      }}
    >
      <ResponsiveFilterGroup
        visible={filterVisible}
        setVisible={setFilterVisible}
        filters={{
          people: search.personIds,
          departments: search.departmentTreeNodes,
          approvalStatuses: search.contractStatusIds,
          contractTypes: search.contractTypeTreeNodes,
          dateRange: search.startDate || search.endDate,
          productions: search.productionIds,
        }}
        onReset={() =>
          updateSearch({
            personIds: undefined,
            departmentTreeNodes: undefined,
            contractStatusIds: undefined,
            contractTypeTreeNodes: undefined,
            startDate: undefined,
            endDate: undefined,
            productionIds: undefined,
          })
        }
      >
        <InfinitePersonMultiSelect
          {...filterProps}
          multiple
          value={search.personIds?.map((id) => id.toString())}
          onSelectionChange={(personIds) =>
            updateSearch({
              personIds: personIds.map((id) => parseInt(id)),
            })
          }
          filterOptions={{ personCategoryTypes: ["Person"] }}
        />
        <DepartmentOrWorkareaTreeSelect
          {...filterProps}
          initialCheckedState={search.departmentTreeNodes}
          onCheckedStateChange={(departmentTreeNodes) =>
            updateSearch({ departmentTreeNodes })
          }
        />
        <ContractApprovalStatusMultiSelect
          {...filterProps}
          value={search.contractStatusIds}
          onChange={(contractStatusIds) => updateSearch({ contractStatusIds })}
        />
        <ContractTypeTreeSelect
          {...filterProps}
          initialCheckedState={search.contractTypeTreeNodes}
          onCheckedStateChange={(contractTypeTreeNodes) =>
            updateSearch({ contractTypeTreeNodes })
          }
        />
        <DateRangePickerInputWithSeasonPresets
          {...filterProps}
          value={dateRange}
          onChange={(newDateRange) =>
            updateSearch({
              startDate: newDateRange[0],
              endDate: newDateRange[1],
            })
          }
        />
        <ProductionMultiSelect
          {...filterProps}
          value={search.productionIds}
          onChange={(productionIds) =>
            updateSearch({
              productionIds,
            })
          }
          period={{
            start: search.startDate,
            end: search.endDate,
          }}
        />
      </ResponsiveFilterGroup>
      <InfiniteScrollingTable
        columnDefinitions={columns}
        infiniteQueryResult={queryResult}
        id={"contracts-table"}
        sortingState={sortingState}
        getRowProps={(row) => ({
          onClick: () =>
            navigate({
              to: "/app/contracts/contracts/$id",
              params: { id: row.original.contractId.toString() },
            }),
        })}
        defaultColumnVisibility={{
          contractId: false,
        }}
      />
    </Page>
  )
}
